import type { Column, EditCommand, EditorFactory, EditorLock } from "../core";
import { ColumnFormat, CompatFormatter, FormatterFactory, GlobalEditorLock, GroupTotals, defaultColumnFormat } from "../core";
import { LayoutEngine } from "./layout";

export interface GridOptions<TItem = any> {
    addNewRowCssClass?: string;
    alwaysAllowHorizontalScroll?: boolean;
    alwaysShowVerticalScroll?: boolean;
    asyncEditorLoadDelay?: number;
    asyncEditorLoading?: boolean;
    asyncPostCleanupDelay?: number;
    asyncPostRenderDelay?: number;
    autoEdit?: boolean;
    autoHeight?: boolean;
    cellFlashingCssClass?: string;
    cellHighlightCssClass?: string;
    emptyNode?: (node: Element) => void;
    columns?: Column<TItem>[];
    createPreHeaderPanel?: boolean;
    dataItemColumnValueExtractor?: (item: TItem, column: Column<TItem>) => void;
    defaultColumnWidth?: number;
    defaultFormat?: ColumnFormat<TItem>;
    defaultFormatter?: CompatFormatter<TItem>;
    editable?: boolean;
    editCommandHandler?: (item: TItem, column: Column<TItem>, command: EditCommand) => void;
    editorCellNavOnLRKeys?: boolean;
    editorFactory?: EditorFactory;
    editorLock?: EditorLock;
    enableAddRow?: boolean;
    enableAsyncPostRender?: boolean;
    enableAsyncPostRenderCleanup?: boolean;
    enableCellNavigation?: boolean;
    enableCellRangeSelection?: boolean;
    enableColumnReorder?: boolean;
    enableRowReordering?: boolean;
    enableTabKeyNavigation?: boolean;
    enableTextSelectionOnCells?: boolean;
    explicitInitialization?: boolean;
    footerRowHeight?: number;
    forceFitColumns?: boolean;
    forceSyncScrolling?: boolean;
    forceSyncScrollInterval?: number;
    formatterFactory?: FormatterFactory;
    frozenBottom?: boolean;
    frozenColumns?: number;
    frozenRows?: number;
    fullWidthRows?: boolean;
    groupingPanel?: boolean,
    groupingPanelHeight?: number;
    groupTotalsFormatter?: (p1?: GroupTotals<TItem>, p2?: Column<TItem>, grid?: any) => string;
    headerRowHeight?: number;
    jQuery?: { ready: any, fn: any };
    leaveSpaceForNewRows?: boolean;
    layoutEngine?: LayoutEngine;
    minBuffer?: number;
    multiColumnSort?: boolean;
    multiSelect?: boolean;
    preHeaderPanelHeight?: number;
    renderAllCells?: boolean;
    renderAllRows?: boolean;
    removeNode?: (node: Element) => void;
    rowHeight?: number;
    rtl?: boolean;
    selectedCellCssClass?: string;
    showCellSelection?: boolean;
    showColumnHeader?: boolean;
    showFooterRow?: boolean;
    showGroupingPanel?: boolean;
    showHeaderRow?: boolean;
    showPreHeaderPanel?: boolean;
    showTopPanel?: boolean;
    slickCompat?: boolean;
    suppressActiveCellChangeOnEdit?: boolean;
    syncColumnCellResize?: boolean;
    topPanelHeight?: number;
    useLegacyUI?: boolean;
    useCssVars?: boolean;
    viewportClass?: string;
}

export const gridDefaults: GridOptions = {
    addNewRowCssClass: "new-row",
    alwaysAllowHorizontalScroll: false,
    alwaysShowVerticalScroll: false,
    asyncEditorLoadDelay: 100,
    asyncEditorLoading: false,
    asyncPostCleanupDelay: 40,
    asyncPostRenderDelay: -1,
    autoEdit: true,
    autoHeight: false,
    cellFlashingCssClass: "flashing",
    dataItemColumnValueExtractor: null,
    defaultColumnWidth: 80,
    defaultFormat: defaultColumnFormat,
    editable: false,
    editorFactory: null,
    editorLock: GlobalEditorLock,
    enableAddRow: false,
    enableAsyncPostRender: false,
    enableAsyncPostRenderCleanup: false,
    enableCellNavigation: true,
    enableColumnReorder: true,
    enableTabKeyNavigation: true,
    enableTextSelectionOnCells: false,
    explicitInitialization: false,
    footerRowHeight: 30,
    forceFitColumns: false,
    forceSyncScrolling: false,
    forceSyncScrollInterval: 250,
    formatterFactory: null,
    fullWidthRows: false,
    groupingPanel: false,
    groupingPanelHeight: 30,
    headerRowHeight: 30,
    leaveSpaceForNewRows: false,
    minBuffer: 3,
    multiColumnSort: false,
    multiSelect: true,
    renderAllCells: false,
    rowHeight: 30,
    selectedCellCssClass: "selected",
    showCellSelection: true,
    showColumnHeader: true,
    showFooterRow: false,
    showGroupingPanel: true,
    showHeaderRow: false,
    showTopPanel: false,
    suppressActiveCellChangeOnEdit: false,
    topPanelHeight: 30,
    useLegacyUI: false,
    useCssVars: false
}
